import { Link, useFetcher, type FetcherWithComponents } from "@remix-run/react";
import {
  PlusCircle,
  Upload,
  Zap,
  Trash2,
  Loader2,
  ImagePlus,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Image,
} from "lucide-react";
import { Img } from 'openimg/react'
import * as React from "react";
import { toast } from "sonner";
import { Button } from "#app/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "#app/components/ui/dialog.tsx";
import { cn, getCdnStationImgSrc, getStationImgSrc } from "#app/utils/misc.tsx";
// Define types (consider moving to a shared types file)
type StationImage = {
  id: string;
  createdAt: string;
  userId: string | null;
  objectKey: string;
  user: {
    username: string | null;
  } | null;
};

// Define a basic User type - replace with your actual User type if available
type User =
  | {
      id: string;
      username?: string | null;
      // Add other relevant user properties
    }
  | null
  | undefined; // Allow null or undefined

interface ImageGalleryProps {
  stationId: number;
  stationName: string | undefined;
  stationUrl: string; // For login redirect
  stationImages: StationImage[] | undefined;
  user: User;
  imageFetcher: FetcherWithComponents<any>; // Typed the fetcher
  imageFormRef: React.RefObject<HTMLFormElement>;
  isSheet: boolean;
}

export function ImageGallery({
  stationId,
  stationName,
  stationUrl,
  stationImages: initialStationImages,
  user,
  imageFetcher,
  imageFormRef,
  isSheet,
}: ImageGalleryProps) {
  const [previewImage, setPreviewImage] = React.useState<string | null>(null);
  const [stationImages, setStationImages] = React.useState<StationImage[]>(
    initialStationImages || [],
  );
  const deleteImageFetcher = useFetcher();
  const [imageToDelete, setImageToDelete] = React.useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const processedDeleteIdRef = React.useRef<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null); // Ref for file input
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = React.useState<
    number | null
  >(null); // State for viewer index
  const [isViewerImageLoading, setIsViewerImageLoading] = React.useState(false); // State for dialog image loading

  React.useEffect(() => {
    // Reset to initial images if fetcher has data but it's not a success (e.g., validation error on reload)
    // Also handle cases where the component might re-render with old fetcher data before a navigation
    if (
      imageFetcher.state === "idle" &&
      imageFetcher.data &&
      imageFetcher.data.status !== 200
    ) {
      // console.log("Resetting images due to non-200 idle fetcher state:", imageFetcher.data);
      setStationImages(initialStationImages || []);
      setPreviewImage(null); // Clear preview on error
      setUploadError(imageFetcher.data.message || "Falha no envio."); // Show specific error if available
    }
    // Explicitly update images if initialStationImages changes (e.g., after navigation/revalidation)
    // But only if not currently uploading or just finished an upload
    if (imageFetcher.state === "idle" && !imageFetcher.data) {
      setStationImages(initialStationImages || []);
    }
  }, [initialStationImages, imageFetcher.state, imageFetcher.data]);

  React.useEffect(() => {
    // Handle successful upload
    if (imageFetcher.state === "idle" && imageFetcher.data?.status === 200) {
      if (imageFetcher.data.newImage) {
        const newImage = {
          ...imageFetcher.data.newImage,
          userId: user?.id,
          user: { username: user?.username || "Você" },
        };
        setStationImages((prevImages) => {
          const imageExists = prevImages.some((img) => img.id === newImage.id);
          // Add image optimistically
          if (!imageExists) {
            return [newImage, ...prevImages];
          }
          return prevImages;
        });
        toast.success("Foto enviada com sucesso!", {
          description: "⚡ Você ganhou +5 pontos!",
        });
      }
      setPreviewImage(null); // Clear preview
      setUploadError(null); // Clear any previous errors
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    // Handle upload error state shown in the UI
    if (imageFetcher.state === "idle" && imageFetcher.data?.status >= 400) {
      setPreviewImage(null); // Clear preview on error
      setUploadError(
        imageFetcher.data.message || "Ocorreu um erro ao enviar a imagem.",
      );
      toast.error("Erro no Envio", {
        description:
          imageFetcher.data.message ||
          "Não foi possível enviar sua foto. Tente novamente.",
      });
      // Keep the file input value in case user wants to retry? Or clear it? Clearing seems better UX.
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [imageFetcher.state, imageFetcher.data, user]);

  // Handle successful deletion
  React.useEffect(() => {
    if (deleteImageFetcher.data && deleteImageFetcher.state === "idle") {
      const response = deleteImageFetcher.data as any; // Type this properly if possible
      if (
        response?.status === 200 &&
        imageToDelete &&
        imageToDelete !== processedDeleteIdRef.current
      ) {
        // Optimistically remove the image
        setStationImages((prev) =>
          prev.filter((image) => image.id !== imageToDelete),
        );

        toast.success("Imagem excluída com sucesso", {
          description: "⚠️ Você perdeu 5 pontos.",
        });

        processedDeleteIdRef.current = imageToDelete; // Mark as processed
        setImageToDelete(null); // Clear state
      } else if (response?.message && response?.status !== 200) {
        // Handle deletion error
        toast.error("Erro ao excluir imagem", {
          description: response.message,
        });
        setImageToDelete(null); // Clear state even on error
      }
      // Always close dialog after attempt
      setDeleteDialogOpen(false);
    }
  }, [deleteImageFetcher.data, deleteImageFetcher.state, imageToDelete]);

  // Handler for file input change - sets preview
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setUploadError(null); // Clear previous errors on new selection

    if (file) {
      // Basic client-side validation (example: size)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        setUploadError("O arquivo é muito grande (máx 20MB).");
        setPreviewImage(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return; // Stop processing
      }

      // Basic client-side validation (example: type)
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/heic",
      ];
      if (!allowedTypes.includes(file.type)) {
        setUploadError("Tipo de arquivo inválido (use JPG, PNG, WEBP, HEIC).");
        setPreviewImage(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return; // Stop processing
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
        // --- REMOVED: No automatic submission ---
        // if (imageFormRef.current) {
        //     imageFetcher.submit(imageFormRef.current);
        // }
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewImage(null);
    }
  };

  const isUploading = imageFetcher.state !== "idle";

  // --- Navigation Handlers ---
  const openImageViewer = (index: number) => {
    setCurrentImageIndex(index);
  };

  const closeImageViewer = () => {
    setCurrentImageIndex(null);
  };

  const goToPreviousImage = (
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event?.stopPropagation(); // Prevent dialog closure if clicking inside
    setCurrentImageIndex((prevIndex) =>
      prevIndex !== null && prevIndex > 0 ? prevIndex - 1 : prevIndex,
    );
  };

  const goToNextImage = (
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event?.stopPropagation(); // Prevent dialog closure if clicking inside
    setCurrentImageIndex((prevIndex) =>
      prevIndex !== null &&
      stationImages &&
      prevIndex < stationImages.length - 1
        ? prevIndex + 1
        : prevIndex,
    );
  };

  // Keyboard navigation effect
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (currentImageIndex === null) return;

      if (event.key === "ArrowLeft") {
        goToPreviousImage();
      } else if (event.key === "ArrowRight") {
        goToNextImage();
      } else if (event.key === "Escape") {
        closeImageViewer();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // Rerun effect if index or images change (important for next/prev limits)
  }, [currentImageIndex, stationImages]);

  // Helper to get current image data safely
  const currentImageData =
    currentImageIndex !== null && stationImages
      ? stationImages[currentImageIndex]
      : null;

  // Effect to reset viewer loading state when image changes
  React.useEffect(() => {
    if (currentImageData) {
      setIsViewerImageLoading(true);
    }
  }, [currentImageData?.id]); // Depend on the specific image ID

  return (
    <div className="w-full rounded-lg border border-border bg-white shadow-sm">
      {/* Header: Match StationInfo style with icon and adjusted padding/border */}
      <div className="p-5">
        <div className="flex items-center gap-2">
          <Image className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">
            Fotos da Estação
          </h3>
        </div>
      </div>

      {/* Gallery Grid: Adjust padding to px-5 */}
      <div className="px-5 pb-5">
        {/* Conditional rendering for empty state vs gallery */}
        {stationImages && stationImages.length > 0 ? (
          <div className="grid h-[280px] grid-cols-2 gap-3 overflow-y-auto rounded-md border border-gray-200 p-3 dark:border-gray-700 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3">
            {stationImages.map((image, index) => (
              <div key={image.id} className="group relative aspect-square">
                <div
                  className="h-full w-full cursor-pointer overflow-hidden rounded-md transition-all duration-200 ease-in-out group-hover:brightness-110"
                  onClick={() => openImageViewer(index)} // Trigger opens viewer with index
                >
                  <Img
                    loading={!isSheet || index < 6 ? "eager" : "lazy"} // Eager load first few images
                    className="h-full w-full object-cover "
                    src={`${getCdnStationImgSrc(image.objectKey)}`}
                    width={300}
                    height={300}
                    alt={`Foto ${index + 1} da estação ${stationName}`}
                  />
                </div>
                {/* Delete Button - visible only to image owner */}
                {user?.id === image.userId && (
                  <Button
                    variant="destructive" // More prominent delete style
                    size="icon"
                    className="absolute bottom-1.5 right-1.5 z-10 h-7 w-7" // Always visible
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent dialog from opening when clicking delete
                      setImageToDelete(image.id);
                      setDeleteDialogOpen(true);
                    }}
                    aria-label="Excluir imagem"
                    // Disable if another delete is in progress
                    disabled={deleteImageFetcher.state !== "idle"}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Enhanced Empty State
          <div className="flex h-[280px] flex-col items-center justify-center rounded-md border border-dashed border-gray-300 bg-muted/20 p-6 text-center dark:border-gray-600 dark:bg-gray-700/50">
            <ImagePlus
              className="h-16 w-16 text-gray-400 dark:text-gray-500"
              strokeWidth={1}
            />
            <p className="mt-4 text-lg font-medium text-gray-700 dark:text-gray-300">
              Adicione a primeira foto!
            </p>
            <p className="mt-1 flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400">
              Compartilhe fotos e ganhe +5 pontos{" "}
              <Zap fill="currentColor" className="h-4 w-4 text-blue-500" />
            </p>
          </div>
        )}
      </div>

      {/* Upload Section: Adjust padding and add top border */}
      <div className="border-t border-border p-5 dark:border-gray-700">
        {user ? (
          <imageFetcher.Form
            ref={imageFormRef}
            encType="multipart/form-data"
            method="post"
            className="w-full space-y-3"
            // onSubmit={(e) => { /* onSubmit logic handled by button now */ }}
          >
            <input type="hidden" name="locationId" value={stationId} />
            <input type="hidden" name="userId" value={user?.id ?? ""} />
            <input type="hidden" name="intent" value="image" />

            {/* File Input & Dropzone Label */}
            <label
              htmlFor="file"
              className={cn(
                "relative flex h-28 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-muted/20 text-center transition hover:border-blue-500 hover:bg-blue-50 dark:border-gray-600 dark:bg-gray-700/50 dark:hover:border-blue-600 dark:hover:bg-gray-700",
                // Keep styling for preview and error, but remove uploading style from label
                previewImage && "border-solid p-1",
                uploadError &&
                  "border-red-500 bg-red-50 dark:border-red-600 dark:bg-red-900/30", // Error state styling
              )}
            >
              <input
                ref={fileInputRef}
                id="file"
                type="file"
                name="file"
                accept="image/jpeg, image/png, image/webp, image/heic"
                onChange={handleFileChange}
                className="sr-only"
                required
                // Input doesn't need to be disabled during upload anymore, button handles it
              />

              {/* Conditional Content: Preview, Default, Error */}
              {previewImage && !uploadError ? (
                <img
                  src={previewImage}
                  alt="Prévia da imagem"
                  className="h-full w-full rounded-md object-contain"
                />
              ) : uploadError ? (
                <div className="flex flex-col items-center justify-center text-red-600 dark:text-red-400">
                  <AlertCircle className="mb-2 h-8 w-8" />
                  <p className="text-sm font-medium">Erro</p>
                  <p className="text-xs">{uploadError}</p>
                  <p className="mt-1 text-xs underline">Tente outro arquivo</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                  <Upload className="mb-2 h-8 w-8" />
                  <p className="text-sm font-medium">
                    Clique ou toque para carregar
                  </p>
                  <p className="text-xs">PNG, JPG, WEBP, HEIC (Máx 20MB)</p>
                </div>
              )}
            </label>

            {/* Conditional Submit Button */}
            {previewImage && !uploadError && (
              <Button
                size="lg"
                className="w-full flex items-center justify-center gap-2"
                type="submit"
                disabled={isUploading} // Disable only when uploading
              >
                {isUploading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Upload className="h-5 w-5" />
                )}
                {isUploading ? "Enviando..." : "Enviar Foto"}
              </Button>
            )}

            {/* Helper Text - Adjusted */}
            {!previewImage && !uploadError && (
              <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                Selecione uma foto para enviar.
              </p>
            )}
          </imageFetcher.Form>
        ) : (
          // Consistent Logged Out State
          <div className="mt-6 rounded-lg border border-primary/20 bg-primary/5 p-6 text-center dark:border-primary/30 dark:bg-primary/10">
            {" "}
            {/* Added mt-6, adjusted padding */}
            <ImagePlus className="mx-auto h-8 w-8 text-primary mb-3" />
            <p className="mb-1 text-lg font-medium text-primary-foreground dark:text-primary-300">
              {" "}
              {/* Adjusted margin */}
              Contribua com Fotos!
            </p>
            <p className="mb-4 text-sm text-primary/80 dark:text-primary/70">
              {" "}
              {/* Adjusted text size and margin */}
              Faça login para adicionar fotos, ganhar pontos e ajudar a
              comunidade.
            </p>
            <Link
              rel="nofollow"
              to={`/login?redirectTo=/estacoes/${stationUrl}${stationId}`}
              className="inline-flex w-auto"
            >
              <Button variant="default" size="lg" className="w-full sm:w-auto">
                {" "}
                {/* Adjusted button variant and size */}
                Entrar ou Criar Conta
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Image Viewer Dialog */}
      <Dialog
        open={currentImageIndex !== null}
        onOpenChange={(open) => !open && closeImageViewer()}
      >
        <DialogContent className="max-w-5xl p-2 sm:p-4 max-h-[95vh]">
          {" "}
          {/* Adjusted padding and max-width */}
          {currentImageData && (
            <>
              <DialogHeader className="px-2 pt-2 sm:px-0 sm:pt-0">
                {" "}
                {/* Adjust padding */}
                <DialogTitle className="text-sm font-normal text-muted-foreground">
                  Enviada por @{currentImageData.user?.username || "Usuário"} em{" "}
                  {currentImageData.createdAt &&
                    new Date(currentImageData.createdAt).toLocaleDateString(
                      "pt-BR",
                    )}
                </DialogTitle>
                {/* Optional: Close button (default is usually top-right) */}
              </DialogHeader>
              <div className="relative flex items-center justify-center">
                {/* Previous Button */}
                {currentImageIndex !== null && currentImageIndex > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-1 top-1/2 z-20 -translate-y-1/2 transform rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white sm:left-3"
                    onClick={goToPreviousImage}
                    aria-label="Imagem anterior"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                )}

                {/* Image Display - Set fixed height on wrapper */}
                <div className="relative flex h-[80vh] items-center justify-center overflow-hidden"> {/* Set fixed height */}
                  {" "}
                  {/* Ensure container has min-height and centers spinner */}
                  {/* Loading Spinner Overlay */}
                  {isViewerImageLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                      <Loader2 className="h-10 w-10 animate-spin text-gray-500 dark:text-gray-400" />
                    </div>
                  )}
                  <Img
                    key={currentImageData.id} // Add key for potential re-renders when image changes
                    loading="lazy"
                    className="block max-h-[80vh] w-auto rounded-lg object-contain"
                    src={`${getCdnStationImgSrc(currentImageData.objectKey)}`}
                    width={1920}
                    height={1080}
                    alt={`Foto ${currentImageIndex !== null ? currentImageIndex + 1 : ""} da estação ${stationName}`}
                    onLoad={() => setIsViewerImageLoading(false)} // Set loading false on load
                    onError={() => setIsViewerImageLoading(false)} // Also stop loading on error
                  />
                </div>

                {/* Next Button */}
                {currentImageIndex !== null &&
                  stationImages &&
                  currentImageIndex < stationImages.length - 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 z-20 -translate-y-1/2 transform rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white sm:right-3"
                      onClick={goToNextImage}
                      aria-label="Próxima imagem"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </Button>
                  )}
              </div>
              {/* Optional Footer or actions if needed */}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (deleteImageFetcher.state !== "idle") return; // Prevent closing while submitting
          setDeleteDialogOpen(open);
          if (!open) {
            setImageToDelete(null);
            processedDeleteIdRef.current = null; // Reset processed ref on close
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir esta imagem? Você perderá 5 pontos
              e esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex flex-row justify-end gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteImageFetcher.state !== "idle"} // Disable if submitting
            >
              Cancelar
            </Button>
            {/* Separate form for deletion */}
            <deleteImageFetcher.Form method="post" className="flex">
              <input type="hidden" name="intent" value="delete-image" />
              <input type="hidden" name="imageId" value={imageToDelete || ""} />
              <input type="hidden" name="userId" value={user?.id ?? ""} />{" "}
              {/* Use optional chaining with nullish coalescing */}
              <input type="hidden" name="locationId" value={stationId} />
              <Button
                type="submit"
                variant="destructive"
                disabled={deleteImageFetcher.state !== "idle"}
              >
                {deleteImageFetcher.state === "submitting" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                Excluir
              </Button>
            </deleteImageFetcher.Form>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
